import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import HeroSearch from "@/components/HeroSearch";
import ListingCard from "@/components/ListingCard";
import ListingCardSkeleton from "@/components/ListingCardSkeleton";
import { CATEGORY_IMG, HERO_IMG } from "@/lib/categories";
import { BRAND } from "@/lib/brand";
import AdSlot from "@/components/AdSlot";
import { ArrowRight, Sparkles, ShieldCheck, Heart } from "lucide-react";
import { toast } from "sonner";

export default function HomePage() {
  const [counts, setCounts] = useState({});
  const [cats, setCats] = useState(null);
  const [featured, setFeatured] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [catRes, featRes] = await Promise.all([
          api.get("/categories"),
          api.get("/listings/featured?limit=6&fields=summary"),
        ]);
        if (!active) return;
        const map = {};
        catRes.data.categories.forEach((c) => (map[c.name] = c.count));
        setCounts(map);
        setCats(catRes.data.categories || []);
        setFeatured(featRes.data.results || []);
      } catch (e) {
        if (active) { setFeatured([]); setCats([]); }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const totalListings = Object.values(counts).reduce((a, b) => a + b, 0);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/subscribers", { email, source: "home_newsletter" });
      toast.success("You're on the list! Check your inbox soon.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <picture>
            <source srcSet={`${process.env.REACT_APP_BACKEND_URL || ""}/api/brand/hero-colorado.webp`} type="image/webp" />
            <img src={`${process.env.REACT_APP_BACKEND_URL || ""}/api/brand/hero-colorado.jpg`} alt="" width="1584" height="672" className="h-full w-full object-cover" fetchPriority="high" loading="eager" decoding="async" />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/85 via-primary/75 to-primary/40" />
        </div>
        <div className="container-page relative z-10 grid gap-10 py-20 lg:grid-cols-[1.4fr_1fr] lg:py-28">
          <div className="text-primary-foreground animate-fade-in-up">
            <span className="chip mb-6 bg-accent/95 text-accent-foreground">
              <Sparkles className="h-3 w-3" /> Colorado · Black-led care
            </span>
            <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl lg:text-[80px]">
              Connect with trusted{" "}
              <span className="italic text-accent">Black health</span> professionals.
            </h1>
            <p className="mt-6 max-w-xl text-base text-primary-foreground/90 sm:text-lg">
              A curated directory of culturally competent providers, organizations and wellness resources
              across Colorado — built and maintained by the Colorado Black Health Collaborative.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-sm text-primary-foreground/80">
              <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-accent" /> {totalListings || "—"} verified resources</div>
              <span className="opacity-30">•</span>
              <div className="flex items-center gap-2"><Heart className="h-4 w-4 text-accent" /> 18 specialties</div>
              <span className="opacity-30">•</span>
              <div>Free to search, free to list</div>
            </div>
          </div>
          <div className="lg:mt-16">
            <HeroSearch />
            <p className="mt-4 text-xs text-primary-foreground/70">
              Looking for a specific provider? <Link to="/directory" className="font-semibold text-accent underline-offset-4 hover:underline">Browse the full directory →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="categories" className="container-page py-20" data-testid="categories-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Browse by specialty</span>
            <h2 className="mt-3 font-display text-4xl font-bold text-primary lg:text-5xl">Find the right kind of care</h2>
          </div>
          <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent" data-testid="view-all-categories">
            View all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {(cats || []).map((c, idx) => {
            const count = c.count ?? 0;
            const img = c.photo_url || CATEGORY_IMG[c.name];
            // First 6 tiles are above the fold on desktop — load eagerly.
            const eager = idx < 6;
            return (
              <Link
                key={c.id || c.name}
                to={`/directory?category=${encodeURIComponent(c.name)}`}
                className="card-surface card-hover group overflow-hidden"
                data-testid={`category-tile-${c.name}`}
              >
                <div className="relative aspect-square overflow-hidden bg-primary-dark">
                  {img && (
                    <img
                      src={img}
                      alt=""
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading={eager ? "eager" : "lazy"}
                      fetchPriority={eager ? "high" : "auto"}
                      decoding="async"
                      // Deliberately NO onError hider — if the image fails, let the gradient
                      // overlay keep the tile visible with the category name. Hiding the img
                      // used to make tiles "disappear" under flaky load (iter 15).
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-dark/85 via-primary/35 to-primary/15" />
                  <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-display text-base font-bold leading-tight text-primary-foreground">
                      {c.name}
                    </div>
                    <div className="text-[11px] uppercase tracking-wider text-accent-foreground/90">
                      {count} {count === 1 ? "resource" : "resources"}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured */}
      <section className="container-page py-20" data-testid="featured-section">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Featured this month</span>
            <h2 className="mt-3 font-display text-4xl font-bold text-primary lg:text-5xl">Meet our professionals</h2>
            <p className="mt-3 max-w-xl text-foreground/70">
              Trusted Black providers and organizations supporting your wellness journey.
            </p>
          </div>
          <Link to="/directory" className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-accent" data-testid="view-all-featured">
            See all listings <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured === null
            ? Array.from({ length: 6 }).map((_, i) => <ListingCardSkeleton key={i} />)
            : featured.length === 0
              ? <p className="text-muted-foreground">No listings yet — re-run the importer.</p>
              : featured.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      </section>

      {/* Home placement ad slot — placed above the Mission section per spec */}
      <section className="container-page py-4">
        <AdSlot placement="home" variant="banner" />
      </section>

      {/* Our Mission */}
      <section className="container-page py-20" data-testid="mission-section">
        <div className="grid items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-accent-dark">Our Mission</span>
            <h2 className="mt-3 font-display text-4xl font-bold leading-tight text-primary lg:text-5xl">
              Connecting Coloradans with Black-led care.
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-foreground/80">
              We connect Coloradans with Black-led health and wellness providers, organizations, and resources —
              building a stronger, healthier community through visibility and trust. Every listing is community-sourced
              and curated alongside the Colorado Black Health Collaborative.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/directory" className="btn-primary">Explore the directory</Link>
              <Link to="/signup" className="btn-secondary">List your practice</Link>
            </div>
          </div>
          <div className="relative">
            <img
              src={BRAND.community}
              alt="Colorado community at a wellness gathering"
              className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_20px_60px_-20px_rgba(13,71,68,0.45)]"
              data-testid="mission-image"
              loading="lazy"
            />
            <div className="absolute -bottom-5 -right-4 hidden rounded-xl bg-accent px-4 py-3 text-accent-foreground shadow-lg sm:block">
              <div className="font-display text-2xl font-bold leading-none">18</div>
              <div className="text-[10px] uppercase tracking-wider opacity-90">Specialties · 1 directory</div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="container-page py-20" data-testid="newsletter-section">
        <div className="overflow-hidden rounded-3xl bg-primary p-10 text-primary-foreground sm:p-14">
          <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr] lg:items-center">
            <div>
              <span className="chip bg-accent/95 text-accent-foreground">Stay connected</span>
              <h3 className="mt-4 font-display text-3xl font-bold leading-tight sm:text-4xl">
                Monthly resources, new providers, and community wellness in your inbox.
              </h3>
              <p className="mt-3 max-w-xl text-primary-foreground/80">
                One thoughtful email a month — curated, never spammy. Unsubscribe any time.
              </p>
            </div>
            <form onSubmit={subscribe} className="flex flex-col gap-3 sm:flex-row" data-testid="newsletter-form" aria-live="polite">
              {subscribed && (
                <div className="rounded-md bg-accent/95 px-4 py-3 text-sm font-semibold text-accent-foreground sm:absolute sm:-mt-12" data-testid="newsletter-success">
                  ✓ You're subscribed — keep an eye on your inbox.
                </div>
              )}
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full rounded-md bg-primary-foreground/10 px-4 py-3 text-primary-foreground placeholder:text-primary-foreground/50 outline-none ring-1 ring-primary-foreground/20 focus:ring-accent"
                data-testid="newsletter-email-input"
              />
              <button type="submit" disabled={submitting} className="btn-accent !py-3" data-testid="newsletter-submit">
                {submitting ? "Sending…" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
